// src/components/DeleteAccount.js
import axios from '../utils/axiosConfig';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Style/DeleteAccount.css';

const DeleteAccount = () => {
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const navigate = useNavigate();

  const validatePassword = (pwd) => {
    if (!pwd) return 'Password is required.';
    if (pwd.length < 8) return 'Password must be at least 8 characters.';
    if (pwd.length > 128) return 'Password must not exceed 128 characters.';
    if (!/[A-Z]/.test(pwd)) return 'Password must include at least one uppercase letter.';
    if (!/[a-z]/.test(pwd)) return 'Password must include at least one lowercase letter.';
    if (!/[0-9]/.test(pwd)) return 'Password must include at least one number.';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return 'Password must include at least one special character.';
    return '';
  };

  const handleDeleteAccount = async () => {
    const validationError = validatePassword(password);
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');

    try {
      // ✅ Do NOT set Authorization header here — axiosConfig adds `Token <key>` for you.
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/delete_account/`,
        {
          data: { password },
          headers: { 'X-Skip-Auth-Redirect': '1' }, // prevent interceptor kick-out on handled errors
        }
      );

      // Success → log out locally and go home
      localStorage.removeItem('token');
      localStorage.removeItem('user_type');
      localStorage.setItem('logout_at', String(Date.now()));
      setSuccess(true);
      setTimeout(() => navigate('/', { replace: true }), 1500);

    } catch (err) {
      const status = err?.response?.status;
      const msg    = err?.response?.data?.error;

      // Backend best-practice: wrong password should be 400 (not 401)
      if (status === 400) {
        setError(msg || 'Incorrect password. Please try again.');
      } else if (status === 401) {
        // real auth problem (expired/invalid token)
        setError('Your session has expired. Please sign in again.');
      } else if (status === 403) {
        setError('You are not authorized to delete this account.');
      } else if (status === 429) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Server error. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="delete-account-container">
      {success ? (
        <div className="delete-success">
          <h3>Your account has been successfully deleted.</h3>
          <p>Redirecting you to home…</p>
        </div>
      ) : (
        <div className="delete-prompt">
          <h3>Delete Account</h3>

          {!showPasswordInput && (
            <div className="delete-warning">
              <p>
                <strong>Warning:</strong> Deleting your account is <u>irreversible</u>. All your data will be permanently removed.
              </p>
              <button
                onClick={() => { setShowPasswordInput(true); setError(''); }}
                className="confirm-delete-button"
              >
                Yes, I Understand & Delete My Account
              </button>
            </div>
          )}

          {showPasswordInput && (
            <div className="password-input-section">
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="password-input"
                autoComplete="off"
                disabled={loading}
              />
              <p className="password-hint">Enter your password to confirm account deletion.</p>

              <div className="button-group">
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading}
                  className="delete-button"
                >
                  {loading ? 'Deleting…' : 'Delete Account'}
                </button>
              </div>

              {error && <p className="error-message">{error}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeleteAccount;
